import type { Timestamp } from "firebase/firestore";

/** One card returned by fetchDeck (shape defined in functions/src/deck.ts). */
export interface DeckCard {
  uid: string;
  displayName: string;
  age: number;
  headline: string;
  bio: string;
  prompts: { prompt: string; answer: string }[];
  photoURLs: string[];
  locationName: string;
  distanceMiles: number;
  boosted: boolean;
  compatibility: number | null; // 0–100 or null
  responsiveness: number | null; // 0–100 or null (hidden for new users)
}

export type MatchState = "active" | "awaitingPayment" | "expired";
export type Tier = "pack25" | "pack100";
export type Intent = Tier | "declined" | null;

export interface PaywallInfo {
  round: number;
  deadline: Timestamp | null;
  intents: Record<string, Intent>;
  resolvedTier: Tier | null;
  paid: Record<string, boolean>;
}

/** matches/{matchId} — server-written, client read-only. */
export interface MatchDoc {
  participants: string[];
  state: MatchState;
  allotmentPerUser: number;
  used: Record<string, number>;
  streak: { uid: string | null; count: number };
  paywall: PaywallInfo | null;
  createdAt: Timestamp | null;
  lastMessageAt: Timestamp | null;
  lastRead?: Record<string, Timestamp>;
  /** duels: loser must message first */
  firstMessageBy?: string | null;
}

export interface MessageDoc {
  id: string;
  senderId: string;
  text: string;
  sentAt: Timestamp | null;
}

/** Public profile fields we read from users/{uid}. */
export interface UserProfile {
  displayName?: string;
  headline?: string;
  bio?: string;
  gender?: string;
  seeking?: string[];
  prompts?: { prompt: string; answer: string }[];
  photoURLs?: string[];
  searchRadiusMiles?: number;
  ageMin?: number;
  ageMax?: number;
  birthdate?: Timestamp;
  lat?: number;
  lng?: number;
  locationName?: string;
  activeFishbowlId?: string;
  fishbowlCountDate?: string;
  fishbowlCountToday?: number;
}

export type RelayGameType = "trivia" | "headbandz" | "acro";

export interface TriviaQuestion {
  id: string;
  category: string;
  question: string;
  correct: string;
  incorrect: string[];
}

/** fishbowls/{id} — server-written, participant read-only (plus the lobby
 * ready handshake). `names` is NOT on the room doc anymore: identity is
 * gender-gated, so the client fills it from its private views/{uid} subdoc. */
export interface RoomDoc {
  participants: string[];
  names: Record<string, string>;
  genders: Record<string, string>;
  seekings: Record<string, string[]>;
  state: "live" | string;
  ready?: Record<string, boolean>;
  used: Record<string, number>;
  startedAt: Timestamp | null;
  endsAt: Timestamp | null;
  gameType: RelayGameType;
  questions?: TriviaQuestion[];
  acroLetters?: Record<string, string[]>;
  leftParticipants?: string[];
}

export interface RoomMessage {
  id: string;
  senderId: string;
  /** Legacy only — new messages have no name; resolve via your view doc. */
  senderName?: string;
  text: string;
  sentAt: Timestamp | null;
}

export interface PairMessage extends RoomMessage {
  round: number;
  pairId: string;
}

export interface TeamAnswerDoc {
  round: number;
  questionIndex: number;
  pairId: string;
  answer: string;
  lockedBy: string;
}

export const TIER_LABEL: Record<Tier, string> = {
  pack25: "25 more messages each",
  pack100: "100 more messages each",
};
export const TIER_SIZE: Record<Tier, number> = {
  pack25: 25,
  pack100: 100,
};
export const TIER_PRICE: Record<Tier, string> = {
  pack25: "$0.99",
  pack100: "$2.99",
};

/** duels/{id} — server-written, participant read-only (functions/src/duel.ts). */
export interface DuelDoc {
  participants: string[];
  names: Record<string, string>;
  genders: Record<string, string>;
  state: "live" | "matched" | "passed" | "left";
  leftBy?: string;
  noShow?: boolean;
  present?: Record<string, boolean>;
  gameType: RelayGameType;
  startedAt: Timestamp | null;
  endsAt: Timestamp | null;
  choices: Record<string, boolean>;
  ready?: Record<string, boolean>;
  // trivia
  questions?: TriviaQuestion[];
  answers?: Record<string, Record<string, string>>;
  // headbandz — cursors are per half (hbCursor is legacy, read as half 0)
  hbCursor?: number;
  hbCursor0?: number;
  hbCursor1?: number;
  hbScores?: Record<string, number>;
  // acro
  acroLetters?: Record<string, string[]>;
  acroWinners?: Record<string, string>;
  acroScores?: Record<string, number>;
}

export interface ClueMsg {
  id: string;
  kind: "clue" | "guess" | "reveal";
  senderId: string;
  senderName: string;
  text: string;
  sentAt: Timestamp | null;
}

export interface DuelAcroEntry {
  round: number;
  authorId: string;
  sentence: string;
  reaction?: string;
  reactionBy?: string;
}

export const DUEL_REACTIONS = ["😂", "🔥", "👏", "😍", "🤯"];

export const REPORT_REASONS: { value: string; label: string }[] = [
  { value: "inappropriate_messages", label: "Inappropriate messages" },
  { value: "fake_profile", label: "Fake profile" },
  { value: "harassment", label: "Harassment" },
  { value: "underage", label: "Underage" },
  { value: "other", label: "Other" },
];
